import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, MonitorSmartphone, User as UserIcon } from "lucide-react";

import { authApi, type MeResponse } from "@/api/auth";
import { useAuthStore } from "@/stores/auth";
import { cn } from "@/lib/utils";

interface HeaderProps {
  me: MeResponse;
  activeTenantId: string | null;
  onTenantChange: (tenantId: string) => void;
}

export function Header({ me, activeTenantId, onTenantChange }: HeaderProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clearAuth = useAuthStore((s) => s.clear);

  const activeTenant = me.tenants.find((tt) => tt.id === activeTenantId);

  const logoutAll = useMutation({
    mutationFn: authApi.logoutAll,
    onSettled: () => {
      clearAuth();
      queryClient.clear();
      navigate("/login", { replace: true });
    },
  });

  const logout = () => {
    void authApi.logout().catch(() => undefined);
    clearAuth();
    queryClient.clear();
    navigate("/login", { replace: true });
  };

  return (
    <header className="h-16 border-b border-slate-200 bg-white flex items-center justify-between px-6 sticky top-0 z-10">
      {/* Tenant selector */}
      <div className="flex items-center gap-3">
        {me.tenants.length > 1 ? (
          <DropdownMenu
            label={
              <span className="flex items-center gap-2 text-sm text-slate-700">
                <span className="text-slate-400">{t("tenant.switch")}:</span>
                <span className="font-medium">
                  {activeTenant?.name ?? t("tenant.no_active")}
                </span>
                <ChevronDown className="h-4 w-4" />
              </span>
            }
          >
            {me.tenants.map((tt) => (
              <button
                key={tt.id}
                type="button"
                onClick={() => onTenantChange(tt.id)}
                className={cn(
                  "block w-full text-left px-3 py-2 text-sm hover:bg-slate-50",
                  tt.id === activeTenantId && "bg-brand-navy-50 text-brand-navy-700 font-medium",
                )}
              >
                {tt.name}
              </button>
            ))}
          </DropdownMenu>
        ) : (
          <span className="text-sm text-slate-600">
            {activeTenant?.name ?? me.tenants[0]?.name ?? t("tenant.no_active")}
          </span>
        )}
      </div>

      <div className="flex items-center gap-3">
        {/* Language */}
        <DropdownMenu
          label={
            <span className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
              {i18n.language.toUpperCase()}
              <ChevronDown className="h-3.5 w-3.5" />
            </span>
          }
        >
          <button
            type="button"
            onClick={() => i18n.changeLanguage("uk")}
            className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
          >
            {t("lang.uk")}
          </button>
          <button
            type="button"
            onClick={() => i18n.changeLanguage("ru")}
            className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
          >
            {t("lang.ru")}
          </button>
        </DropdownMenu>

        {/* User menu */}
        <DropdownMenu
          label={
            <span className="flex items-center gap-2">
              <span className="h-8 w-8 rounded-full bg-brand-navy-500 text-white flex items-center justify-center text-sm font-medium">
                {me.full_name.charAt(0).toUpperCase()}
              </span>
              <span className="hidden sm:flex flex-col items-start">
                <span className="text-sm font-medium text-slate-700">{me.full_name}</span>
                <span className="text-xs text-slate-400">{me.role}</span>
              </span>
            </span>
          }
          align="right"
        >
          <button
            type="button"
            onClick={() => navigate("/settings")}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            <UserIcon className="h-4 w-4" />
            {t("user_menu.profile")}
          </button>
          <button
            type="button"
            onClick={() => logoutAll.mutate()}
            disabled={logoutAll.isPending}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <MonitorSmartphone className="h-4 w-4" />
            {t("user_menu.logout_all")}
          </button>
          <div className="border-t border-slate-100 my-1" />
          <button
            type="button"
            onClick={logout}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            <LogOut className="h-4 w-4" />
            {t("user_menu.logout")}
          </button>
        </DropdownMenu>
      </div>
    </header>
  );
}

// ---- Inline DropdownMenu (без Radix UI заради простоти Phase 1) ----

interface DropdownMenuProps {
  label: React.ReactNode;
  children: React.ReactNode;
  align?: "left" | "right";
}

function DropdownMenu({ label, children, align = "left" }: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded-md px-2 py-1 hover:bg-slate-50"
      >
        {label}
      </button>
      {open && (
        <div
          className={cn(
            "absolute mt-2 min-w-[180px] rounded-md border border-slate-200 bg-white shadow-lg py-1 z-20",
            align === "right" ? "right-0" : "left-0",
          )}
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      )}
    </div>
  );
}
